import { useState, useEffect } from "react";
import { format, parseISO, addDays } from "date-fns";
import { Plus, Loader2, Trash2, Calendar, Pencil, RefreshCw } from "lucide-react";
import { ImageUpload } from "../../components/ImageUpload";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSchedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [studios, setStudios] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Edit modal state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Add studio mini-dialog state
  const [addStudioOpen, setAddStudioOpen] = useState(false);
  const [newStudio, setNewStudio] = useState({ name: "", location: "", capacity: "" });
  const [addingStudio, setAddingStudio] = useState(false);

  const tomorrow = addDays(new Date(), 1);
  const [formData, setFormData] = useState({
    dance_class_id: "",
    studio_id: "",
    instructor_id: "",
    date: format(tomorrow, "yyyy-MM-dd"),
    start_time: "10:00",
    duration: 60,
    max_capacity: 20,
    price: "",
    discount_percentage: "",
    title: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, classesRes, studiosRes, instructorsRes] = await Promise.all([
        axios.get(`${API}/schedules`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/studios`),
        axios.get(`${API}/instructors`),
      ]);

      setSchedules(schedulesRes.data.schedules || schedulesRes.data || []);
      setClasses(classesRes.data.classes || classesRes.data || []);
      setStudios(studiosRes.data || []);
      setInstructors(instructorsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const startDateTime = new Date(`${formData.date}T${formData.start_time}:00`);
      const endDateTime = new Date(startDateTime.getTime() + formData.duration * 60000);

      const selectedClass = classes.find(c => c.id === formData.dance_class_id);

      await toast.promise(
        axios
          .post(`${API}/admin/schedules`, {
            dance_class_id: formData.dance_class_id,
            studio_id: formData.studio_id || null,
            instructor_id: formData.instructor_id || selectedClass?.instructor_id,
            start_datetime: startDateTime.toISOString(),
            end_datetime: endDateTime.toISOString(),
            max_capacity: Number(formData.max_capacity),
            price: formData.price ? Number(formData.price) : selectedClass?.price,
            discount_percentage: formData.discount_percentage !== "" ? Number(formData.discount_percentage) : null,
            title: formData.title.trim() || null,
          })
          .then(async () => {
            setDialogOpen(false);
            await fetchData();
          }),
        {
          loading: "Creating schedule…",
          success: "Schedule created successfully!",
          error: (err) => err?.response?.data?.detail || "Failed to create schedule",
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerate = () => {
    toast("Regenerate schedules?", {
      description:
        "Rebuilds upcoming class times from your current classes. Schedules that already have bookings are kept untouched.",
      action: {
        label: "Regenerate",
        onClick: () => {
          setRegenerating(true);
          toast.promise(
            axios
              .post(`${API}/admin/schedules/rebuild`)
              .then(async (res) => {
                await fetchData();
                return res.data;
              })
              .finally(() => setRegenerating(false)),
            {
              loading: "Regenerating schedules…",
              success: (data) => data?.message || "Schedules regenerated.",
              error: (err) =>
                err?.response?.data?.detail || "Failed to regenerate schedules.",
            }
          );
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const handleDelete = (schedule) => {
    const label = schedule.title || schedule.dance_class?.name || "this schedule";
    const dateStr = schedule.start_datetime
      ? format(parseISO(schedule.start_datetime), "EEE MMM d, h:mm a")
      : "";

    toast("Cancel this schedule?", {
      description: `${label}${dateStr ? ` on ${dateStr}` : ""}. This cannot be undone.`,
      action: {
        label: "Yes, cancel it",
        onClick: () => {
          toast.promise(
            axios.delete(`${API}/admin/schedules/${schedule.id}`).then(() => fetchData()),
            {
              loading: "Cancelling schedule…",
              success: "Schedule cancelled.",
              error: "Failed to cancel schedule.",
            }
          );
        },
      },
      cancel: {
        label: "Keep",
        onClick: () => {},
      },
    });
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setEditForm({
      instructor_id: schedule.instructor_id || "",
      studio_id: schedule.studio_id || "",
      price: schedule.price != null ? String(schedule.price) : "",
      discount_percentage: schedule.schedule_discount_percentage != null ? String(schedule.schedule_discount_percentage) : "",
      title: schedule.schedule_title || "",
      max_capacity: schedule.max_capacity != null ? String(schedule.max_capacity) : "",
      image: schedule.image || "",
      is_redeemable_with_package: schedule.is_redeemable_with_package ?? true,
      notes: schedule.notes || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditInstructorChange = (instructorId) => {
    const instructor = instructors.find(i => i.id === instructorId);
    const redeemable = instructor ? Boolean(instructor.is_default) : false;
    setEditForm(prev => ({
      ...prev,
      instructor_id: instructorId,
      is_redeemable_with_package: redeemable,
    }));
    toast.info(
      redeemable
        ? "Toggle set to ON — instructor is an EDK instructor."
        : "Toggle set to OFF — instructor is not an EDK instructor.",
      { description: "You can still override the toggle manually before saving." }
    );
  };

  const handleRedeemableToggle = (checked) => {
    setEditForm(prev => ({ ...prev, is_redeemable_with_package: checked }));
    toast.info(
      checked
        ? "Package credits will be accepted for this occurrence."
        : "Package credits will NOT be accepted for this occurrence.",
      { description: "This overrides the instructor default. Save to apply." }
    );
  };

  const handleAddStudio = async (e) => {
    e.preventDefault();
    if (!newStudio.name || !newStudio.location || !newStudio.capacity) return;
    setAddingStudio(true);
    try {
      await toast.promise(
        axios
          .post(`${API}/admin/studios`, {
            name: newStudio.name,
            location: newStudio.location,
            capacity: Number(newStudio.capacity),
            hourly_rate: 0,
          })
          .then(async (res) => {
            const created = res.data;
            await fetchData();
            setEditForm((prev) => ({ ...prev, studio_id: created.id }));
            setNewStudio({ name: "", location: "", capacity: "" });
            setAddStudioOpen(false);
            return created;
          }),
        {
          loading: "Creating studio…",
          success: (created) => `Studio "${created.name}" created and selected.`,
          error: (err) => err?.response?.data?.detail || "Failed to create studio.",
        }
      );
    } finally {
      setAddingStudio(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingSchedule) return;
    setEditSubmitting(true);

    try {
      const payload = {};
      if (editForm.instructor_id) payload.instructor_id = editForm.instructor_id;
      if (editForm.studio_id) payload.studio_id = editForm.studio_id;
      if (editForm.price !== "") payload.price = Number(editForm.price);
      if (editForm.max_capacity !== "") payload.max_capacity = Number(editForm.max_capacity);
      payload.discount_percentage = editForm.discount_percentage !== "" ? Number(editForm.discount_percentage) : null;
      payload.title = editForm.title.trim() || null;
      payload.image = editForm.image || null;
      payload.is_redeemable_with_package = editForm.is_redeemable_with_package;
      payload.notes = editForm.notes || null;

      await toast.promise(
        axios
          .put(`${API}/admin/schedules/${editingSchedule.id}`, payload)
          .then(async () => {
            setEditDialogOpen(false);
            await fetchData();
          }),
        {
          loading: "Saving schedule…",
          success: "Schedule updated!",
          error: (err) => err?.response?.data?.detail || "Failed to update schedule",
        }
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  const statusColors = {
    scheduled: "bg-green-500/20 text-green-400",
    cancelled: "bg-red-500/20 text-red-400",
    completed: "bg-blue-500/20 text-blue-400",
  };

  return (
    <div data-testid="admin-schedules">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Schedules</h1>
          <p className="text-neutral-400">Manage class schedules</p>
        </div>

        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={handleRegenerate}
          disabled={regenerating}
          className="border-neutral-700 text-neutral-200"
        >
          {regenerating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Regenerate
        </Button>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-empire">
              <Plus className="w-4 h-4 mr-2" />
              Add Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Schedule</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Class *</Label>
                <Select
                  value={formData.dance_class_id}
                  onValueChange={(value) => setFormData({ ...formData, dance_class_id: value })}
                  required
                >
                  <SelectTrigger className="bg-neutral-900 border-neutral-800">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-empire"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="input-empire"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Studio</Label>
                  <Select
                    value={formData.studio_id}
                    onValueChange={(value) => setFormData({ ...formData, studio_id: value })}
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800">
                      <SelectValue placeholder="Select studio" />
                    </SelectTrigger>
                    <SelectContent>
                      {studios.map((studio) => (
                        <SelectItem key={studio.id} value={studio.id}>
                          {studio.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Instructor</Label>
                  <Select
                    value={formData.instructor_id}
                    onValueChange={(value) => setFormData({ ...formData, instructor_id: value })}
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800">
                      <SelectValue placeholder="Select instructor" />
                    </SelectTrigger>
                    <SelectContent>
                      {instructors.map((instructor) => (
                        <SelectItem key={instructor.id} value={instructor.id}>
                          {instructor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Max Capacity</Label>
                  <Input
                    type="number"
                    value={formData.max_capacity}
                    onChange={(e) => setFormData({ ...formData, max_capacity: e.target.value })}
                    className="input-empire"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Price (KES)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="input-empire"
                    placeholder="Use class price"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Schedule Discount (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                    className="input-empire"
                    placeholder="Use class discount"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Schedule Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-empire"
                  placeholder="Use class title"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-neutral-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-empire"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Schedule"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Add Studio mini-dialog */}
      <Dialog open={addStudioOpen} onOpenChange={setAddStudioOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Studio / Venue</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStudio} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Name *</Label>
              <Input
                value={newStudio.name}
                onChange={(e) => setNewStudio(s => ({ ...s, name: e.target.value }))}
                className="input-empire"
                placeholder="e.g. Studio A"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Location *</Label>
              <Input
                value={newStudio.location}
                onChange={(e) => setNewStudio(s => ({ ...s, location: e.target.value }))}
                className="input-empire"
                placeholder="e.g. Kilimani, Nairobi"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Capacity *</Label>
              <Input
                type="number"
                value={newStudio.capacity}
                onChange={(e) => setNewStudio(s => ({ ...s, capacity: e.target.value }))}
                className="input-empire"
                placeholder="e.g. 20"
                required
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddStudioOpen(false)}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addingStudio} className="flex-1 btn-empire">
                {addingStudio ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & Select"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Modal */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit Schedule — {editingSchedule?.dance_class?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Instructor</Label>
              <Select
                value={editForm.instructor_id || ""}
                onValueChange={handleEditInstructorChange}
              >
                <SelectTrigger className="bg-neutral-900 border-neutral-800">
                  <SelectValue placeholder="Select instructor" />
                </SelectTrigger>
                <SelectContent>
                  {instructors.map((instructor) => (
                    <SelectItem key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Studio / Venue</Label>
              <Select
                value={editForm.studio_id || ""}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, studio_id: value }))}
              >
                <SelectTrigger className="bg-neutral-900 border-neutral-800">
                  <SelectValue placeholder="Select studio" />
                </SelectTrigger>
                <SelectContent>
                  {studios.map((studio) => (
                    <SelectItem key={studio.id} value={studio.id}>
                      {studio.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAddStudioOpen(true)}
                className="text-empire-gold hover:text-empire-gold/80 px-0 h-auto py-0 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add new venue
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Price (KES)</Label>
                <Input
                  type="number"
                  value={editForm.price}
                  onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                  className="input-empire"
                  placeholder={`Class default: ${editingSchedule?.dance_class?.price ?? "—"}`}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Max Capacity</Label>
                <Input
                  type="number"
                  value={editForm.max_capacity}
                  onChange={(e) => setEditForm(prev => ({ ...prev, max_capacity: e.target.value }))}
                  className="input-empire"
                  placeholder={`Class default: ${editingSchedule?.dance_class?.max_capacity ?? "—"}`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Schedule Discount (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={editForm.discount_percentage}
                onChange={(e) => setEditForm(prev => ({ ...prev, discount_percentage: e.target.value }))}
                className="input-empire"
                placeholder={`Class default: ${editingSchedule?.dance_class?.discount_percentage ?? "none"}`}
              />
              <p className="text-neutral-500 text-xs">Leave blank to use the class discount. Enter 0 to disable it for this schedule.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Schedule Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="input-empire"
                placeholder={`Class default: ${editingSchedule?.dance_class?.name ?? "—"}`}
              />
              <p className="text-neutral-500 text-xs">Leave blank to use the dance class title.</p>
            </div>

            <ImageUpload
              label="Override Banner Image (leave blank to use class default)"
              value={editForm.image}
              onChange={(url) => setEditForm(prev => ({ ...prev, image: url }))}
              folder="schedules"
              placeholder="Upload or paste image URL"
            />

            <div className="flex items-center justify-between rounded-lg border border-neutral-800 p-3">
              <div>
                <p className="text-white text-sm font-medium">Redeemable with Package</p>
                <p className="text-neutral-500 text-xs">
                  Auto-set from instructor; override manually if needed
                </p>
              </div>
              <Switch
                checked={editForm.is_redeemable_with_package ?? false}
                onCheckedChange={handleRedeemableToggle}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">
                Admin Notes
                <span className="ml-1 text-xs text-neutral-500 font-normal">(internal only)</span>
              </Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                className="input-empire resize-none"
                rows={3}
                placeholder="Notes visible only to admins…"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 border-neutral-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={editSubmitting}
                className="flex-1 btn-empire"
              >
                {editSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedules Table */}
      <Card className="card-empire">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-empire-gold" />
            Upcoming Schedules
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              No schedules found. Create your first schedule!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800">
                    <TableHead className="text-neutral-400">Class</TableHead>
                    <TableHead className="text-neutral-400">Date & Time</TableHead>
                    <TableHead className="text-neutral-400">Instructor</TableHead>
                    <TableHead className="text-neutral-400">Studio</TableHead>
                    <TableHead className="text-neutral-400">Bookings</TableHead>
                    <TableHead className="text-neutral-400">Price</TableHead>
                    <TableHead className="text-neutral-400">Status</TableHead>
                    <TableHead className="text-neutral-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule.id} className="border-neutral-800">
                      <TableCell className="text-white font-medium">
                        {schedule.title || schedule.dance_class?.name || "Unknown"}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {schedule.start_datetime && (
                          <>
                            <div>{format(parseISO(schedule.start_datetime), "EEE, MMM d")}</div>
                            <div className="text-sm text-neutral-500">
                              {format(parseISO(schedule.start_datetime), "h:mm a")}
                            </div>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {schedule.instructor?.name || "-"}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {schedule.studio?.name || "-"}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {schedule.current_bookings || 0} paid
                        {schedule.pending_bookings > 0 && (
                          <span className="text-yellow-500/80">
                            {" "}· {schedule.pending_bookings} unpaid
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-empire-gold font-medium">
                        KES {(schedule.price || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[schedule.status]}>
                          {schedule.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(schedule)}
                            className="text-neutral-400 hover:text-empire-gold"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(schedule)}
                            className="text-neutral-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
